import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Pencil, Plus, Trash2 } from 'lucide-react'
import {
  templatesQueryOptions,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from '@/utils/templateQueryOptions'
import type { MessageTemplate } from '@/types/templateTypes'
import type { BroadcastPriority } from '@/types/broadcastTypes'
import { useShellStore } from '@/stores/shellStore'
import { useUserStore } from '@/stores/userStore'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * Super-admin CRUD for the shared templates. Opened from the account menu or
 * from the "Manage templates" item at the bottom of the TemplatePicker. The
 * backend re-checks super-admin on every write — this dialog only hides the UI.
 */
export function ManageTemplatesDialog() {
  const open = useShellStore((s) => s.templatesOpen)
  const close = useShellStore((s) => s.closeTemplates)
  const isSuperAdmin = useUserStore((s) => s.User?.isSuperAdmin ?? false)
  const queryClient = useQueryClient()

  // null = list view; a template = editing it; 'new' = creating one.
  const [editing, setEditing] = useState<MessageTemplate | 'new' | null>(null)
  const [name, setName] = useState('')
  const [body, setBody] = useState('')
  const [priority, setPriority] = useState<BroadcastPriority>('normal')
  const [error, setError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<MessageTemplate | null>(null)

  const { data, status } = useQuery({ ...templatesQueryOptions(), enabled: open })
  const templates = data?.templates ?? []

  const backToList = () => {
    setEditing(null)
    setName('')
    setBody('')
    setPriority('normal')
    setError(null)
  }

  const onOpenChange = (o: boolean) => {
    if (!o) {
      close()
      backToList()
    }
  }

  const startNew = () => {
    setEditing('new')
    setName('')
    setBody('')
    setPriority('normal')
    setError(null)
  }

  const startEdit = (t: MessageTemplate) => {
    setEditing(t)
    setName(t.name)
    setBody(t.body)
    setPriority(t.priority)
    setError(null)
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['templates'] })

  const saveMutation = useMutation({
    mutationFn: () => {
      const input = { name: name.trim(), body: body.trim(), priority }
      return editing === 'new' || editing === null
        ? createTemplate(input)
        : updateTemplate(editing.id, input)
    },
    onSuccess: () => {
      invalidate()
      backToList()
    },
    onError: (e: Error) => setError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (t: MessageTemplate) => deleteTemplate(t.id),
    onSuccess: () => {
      invalidate()
      setPendingDelete(null)
    },
    onError: (e: Error) => {
      setError(e.message)
      setPendingDelete(null)
    },
  })

  const canSave = name.trim().length > 0 && body.trim().length > 0 && !saveMutation.isPending

  // Non-super-admins can't reach this dialog through the UI; render nothing if
  // they somehow do rather than showing controls every write would reject.
  if (!isSuperAdmin) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Cap the height and give the middle row minmax(0,1fr) so the list
            scrolls instead of pushing the footer off a small screen. */}
        <DialogContent className="max-h-[85vh] max-w-2xl grid-rows-[auto_minmax(0,1fr)_auto]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit template' : 'Message templates'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Everyone sending a message will see this template.'
                : templates.length > 0
                  ? `${templates.length} template${templates.length === 1 ? '' : 's'}, shared with everyone — pick one instead of retyping a full message.`
                  : 'Shared with everyone — pick one instead of retyping a full message.'}
            </DialogDescription>
          </DialogHeader>

          {editing ? (
            <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label htmlFor="templateName">Name</Label>
                <Input
                  id="templateName"
                  placeholder="e.g. Snow day closure"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="templateBody">Message</Label>
                <Textarea
                  id="templateBody"
                  placeholder="The message that gets inserted…"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="min-h-[140px]"
                />
                <p className="text-xs text-muted-foreground">{body.length} characters</p>
              </div>

              <div className="space-y-1.5">
                <Label>Default priority</Label>
                <div className="inline-flex gap-[3px] rounded-[10px] border border-border bg-surface-2 p-[3px]">
                  <button
                    type="button"
                    onClick={() => setPriority('normal')}
                    className={cn(
                      'rounded-lg px-3.5 py-[7px] text-[12.5px] font-bold transition-colors',
                      priority === 'normal'
                        ? 'bg-surface text-foreground shadow-wha-sm'
                        : 'text-muted-foreground',
                    )}
                  >
                    Normal
                  </button>
                  <button
                    type="button"
                    onClick={() => setPriority('urgent')}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-[7px] text-[12.5px] font-bold transition-colors',
                      priority === 'urgent'
                        ? 'bg-destructive text-white'
                        : 'text-muted-foreground',
                    )}
                  >
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full',
                        priority === 'urgent' ? 'bg-white' : 'bg-destructive',
                      )}
                    />
                    Urgent
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Sets the broadcast priority when the template is used. The sender can still
                  change it.
                </p>
              </div>

              {error && (
                <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-h-0 flex-col gap-3">
              {status === 'pending' ? (
                <Skeleton className="h-48 w-full" />
              ) : templates.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border px-6 py-10 text-center">
                  <FileText className="mx-auto h-6 w-6 text-faint" />
                  <p className="mt-2 text-sm font-semibold">No templates yet</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Add the messages you send often — everyone can then pick them instead of
                    retyping.
                  </p>
                </div>
              ) : (
                // Plain overflow container, not ScrollArea: its viewport is h-full,
                // which collapses to auto inside a max-height parent, so the list
                // clipped instead of scrolling.
                <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-border">
                  {templates.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-start gap-3 border-b border-border-2 px-3.5 py-3 transition-colors last:border-b-0 hover:bg-surface-2"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1.5 text-sm font-bold">
                          <span className="min-w-0 truncate">{t.name}</span>
                          {t.priority === 'urgent' && (
                            <span className="flex-none rounded-full bg-destructive/10 px-1.5 py-px text-[10px] font-bold uppercase text-destructive">
                              Urgent
                            </span>
                          )}
                        </p>
                        {/* Clamped so every row stays the same compact height —
                            the full text is one click away in the editor. */}
                        <p className="mt-1 line-clamp-2 text-xs leading-[1.5] text-muted-foreground">
                          {t.body}
                        </p>
                      </div>
                      <div className="flex flex-none gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(t)}
                          aria-label={`Edit ${t.name}`}
                          title="Edit"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-surface hover:text-foreground"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(t)}
                          aria-label={`Delete ${t.name}`}
                          title="Delete"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {error && (
                <div className="flex-none rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {editing ? (
              <>
                <Button variant="outline" onClick={backToList}>
                  Cancel
                </Button>
                <Button onClick={() => saveMutation.mutate()} disabled={!canSave}>
                  {saveMutation.isPending
                    ? 'Saving…'
                    : editing === 'new'
                      ? 'Create template'
                      : 'Save changes'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
                <Button onClick={startNew}>
                  <Plus className="mr-2 h-4 w-4" />
                  New template
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingDelete != null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              It disappears from everyone’s template list. Messages already sent are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
